import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FaUser, FaLock, FaSignInAlt } from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import { isValidEmail } from '../../utils/validators';

/**
 * Login component for user authentication
 */
const Login = () => {
  const { login, isAuthenticated, user, loading } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);
  
  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user && !loading) {
      // Get return URL from location state or default to dashboard based on role
      const returnUrl = location.state?.returnUrl;
      
      if (returnUrl) {
        navigate(returnUrl);
        return;
      }
      
      // Redirect to appropriate dashboard based on user role
      if (user.role === 'system_admin') {
        navigate('/admin/dashboard');
      } else if (user.role === 'school_admin') {
        navigate('/school/dashboard');
      } else if (user.role === 'teacher') {
        navigate('/teacher/dashboard');
      } else if (user.role === 'parent') {
        navigate('/parent/dashboard');
      } else if (user.role === 'mentor') {
        navigate('/mentor/dashboard');
      } else {
        navigate('/');
      }
    }
  }, [isAuthenticated, user, navigate, location.state, loading]);
  
  // Show message if redirected with message
  useEffect(() => {
    if (location.state?.message) {
      showToast(location.state.message, 'info');
      
      // Clear the location state to prevent showing message again on refresh
      navigate(location.pathname, { replace: true });
    }
    
    // Check for session=expired parameter
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('session') === 'expired') {
      showToast('Your session has expired. Please log in again.', 'info');
      // Remove the query parameter
      navigate(location.pathname, { replace: true });
    }
  }, [location, showToast, navigate]);
  
  /**
   * Handle form input changes
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear errors when user types
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };
  
  /**
   * Validate the form
   */
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const result = await login(formData.email, formData.password);
      
      if (!result.success) {
        // Increment attempt count
        setAttemptCount(prevCount => prevCount + 1);
        
        // Handle specific teacher-related errors
        if (result.error && result.error.toLowerCase().includes('teacher')) {
          setErrors({ 
            general: 'Teacher login failed. If your account was recently created by your school administrator, ' +
                    'please ensure you have the correct password or contact your administrator for assistance.'
          });
        } else {
          setErrors({ general: result.error || 'Login failed. Please check your credentials and try again.' });
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      
      // Increment attempt count
      setAttemptCount(prevCount => prevCount + 1);

      // Handle chunk loading errors
      if (error instanceof Error && error.message.includes('ChunkLoadError')) {
        setErrors({ 
          general: 'We encountered a technical issue. Please try refreshing the page or clearing your browser cache.'
        });
      } else {
        setErrors({ 
          general: error.response?.data?.message || 
                  'Failed to login. Please check your credentials and try again.' 
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Show extra help if multiple failed attempts
  const showExtraHelp = attemptCount >= 2;
  
  return (
    <>
      <Helmet>
        <title>Login | Meira</title>
      </Helmet>
      
      <div className="min-h-[calc(100vh-150px)] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8 bg-white shadow-lg rounded-xl p-8">
          <div>
            <img 
              className="mx-auto h-12 w-auto" 
              src="https://res.cloudinary.com/digitaliquids-cloud-technologies/image/upload/2_xwp1oc.png" 
              alt="Meira Logo" 
            />
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Sign in to your account
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Or{' '}
              <Link to="/register" className="font-medium text-primary hover:text-primary-dark">
                create a new account
              </Link>
            </p>
          </div>
          
          {errors.general && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4" role="alert">
              <p className="text-red-700">{errors.general}</p>
            </div>
          )}
          
          {showExtraHelp && (
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4" role="alert">
              <p className="text-sm text-blue-700 font-medium mb-1">Having trouble logging in?</p>
              <ul className="text-sm text-blue-700 list-disc list-inside">
                <li>For teachers: If your account was created by your school administrator, make sure you have the correct password.</li>
                <li>Try clearing your browser cache or using a different browser.</li>
                <li>Contact your administrator for help with login credentials.</li>
              </ul>
            </div>
          )}
          
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="rounded-md -space-y-px">
              <div className="mb-4">
                <label htmlFor="email" className="flex items-center text-sm font-medium text-gray-700 mb-1">
                  <FaUser className="mr-2" /> Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className={`appearance-none relative block w-full px-3 py-2 border ${
                    errors.email ? 'border-red-300' : 'border-gray-300'
                  } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm`}
                  placeholder="Email address"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>
              
              <div className="mb-4">
                <label htmlFor="password" className="flex items-center text-sm font-medium text-gray-700 mb-1">
                  <FaLock className="mr-2" /> Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className={`appearance-none relative block w-full px-3 py-2 border ${
                    errors.password ? 'border-red-300' : 'border-gray-300'
                  } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm`}
                  placeholder="Password"
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                )}
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="text-sm">
                <Link to="/forgot-password" className="font-medium text-primary hover:text-primary-dark">
                  Forgot your password?
                </Link>
              </div>
            </div>
            
            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                  <FaSignInAlt className="h-5 w-5 text-primary-light group-hover:text-white" aria-hidden="true" />
                </span>
                {isSubmitting ? 'Signing in...' : 'Sign in'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default Login;
